import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { logger } from '../../utils/logger';
import { CLIENT_ID } from '../../lib/client-context';
import { topicKeys } from './topicKeys';
import {
  TopicData,
  FormattedTopic,
  CreateTopicInput,
  UpdateTopicInput,
  AppendMessageInput,
  formatTopic,
} from './topicTypes';
import { db } from '../../lib/offline-db';
import { queueOfflineOperation } from '../../lib/offline-sync';
import { getConnectionState } from '../../contexts/ConnectionStateContext';
import { useQueryErrorHandler } from '../useQueryErrorHandler';
import {
  invalidateQuery,
  safeUpdateQueryData,
  batchInvalidateQueries,
} from '../../utils/queryInvalidationUtils';
// Loading screen should be managed at the component level only

/**
 * Hook for fetching topics for a specific project
 * Supports offline mode by using IndexedDB when offline
 * Uses the byProject query key pattern for consistent caching across components
 */
export function useTopics(projectId: string | null, activeTopicId?: string | null) {
  // Get connection state without causing hook instance creation
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  return useQuery({
    // Use byProject query key directly - crucial for proper caching
    queryKey: projectId ? topicKeys.byProject(projectId) : topicKeys.lists(),
    queryFn: async () => {
      if (!projectId) {
        throw new Error('Project ID is required');
      }

      const fetchId = Date.now().toString().slice(-4);
      logger.fetch(
        `Starting topics fetch #${fetchId} for project ${projectId} (online: ${isOnline})`
      );
      const startTime = performance.now();

      try {
        // If offline, get data from IndexedDB
        if (!isOnline) {
          logger.fetch(`Offline mode: fetching topics from IndexedDB #${fetchId}`);

          try {
            // Get topics from IndexedDB
            const topics = await db.topics
              .where('project_id')
              .equals(projectId)
              .and(topic => !topic.isDeleted) // Filter out deleted topics
              .toArray();

            // Sort by last_used_at
            topics.sort((a, b) => {
              if (!a.last_used_at) return 1;
              if (!b.last_used_at) return -1;
              return new Date(b.last_used_at).getTime() - new Date(a.last_used_at).getTime();
            });

            logger.fetch(`Offline mode: loaded ${topics.length} topics from IndexedDB`);

            // Format topics using the helper function
            return topics.map(topic => formatTopic(topic as TopicData, activeTopicId));
          } catch (offlineError) {
            logger.error(`Error fetching offline topics #${fetchId}:`, offlineError);
            throw new Error('Failed to load topics from offline storage');
          }
        }

        // Online flow - fetch from server
        const { data, error } = await supabase
          .from('topics')
          .select('*')
          .eq('project_id', projectId)
          .order('last_used_at', { ascending: false, nullsLast: true });

        if (error) throw error;

        // Store in IndexedDB for offline access
        try {
          if (data && data.length > 0) {
            await db.topics.bulkPut(data);
            logger.fetch(`Stored ${data.length} topics in IndexedDB`);
          }
        } catch (storageError) {
          logger.error(`Error storing topics in IndexedDB:`, storageError);
          // Continue even if storage fails - this is non-critical
        }

        logger.fetch(`Topics fetch completed successfully with ${data.length} topics`);

        // Format topics using the helper function
        const formattedTopics = (data || []).map(topic =>
          formatTopic(topic as TopicData, activeTopicId)
        );

        // Log performance metrics
        const endTime = performance.now();
        const duration = endTime - startTime;
        logger.fetch(`Topics fetch #${fetchId} completed in ${duration.toFixed(2)}ms`);

        return formattedTopics;
      } catch (error) {
        // Use the error handler to handle and log the error
        const appError = handleError(error, {
          action: 'silent',
          context: {
            fetchId,
            operation: 'fetchTopics',
            projectId,
            isOnline,
          },
        });

        throw appError;
      }
    },
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000, // 5 minutes - keep cached longer
  });
}

/**
 * Hook for fetching a single topic by ID
 * Supports offline mode by using IndexedDB when offline
 */
export function useTopic(topicId: string | null) {
  // Get connection state without causing hook instance creation
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  return useQuery({
    queryKey: topicId ? topicKeys.detail(topicId) : topicKeys.details(),
    queryFn: async () => {
      if (!topicId) {
        throw new Error('Topic ID is required');
      }

      logger.fetch(`Fetching topic ${topicId} (online: ${isOnline})`);

      try {
        // If offline, get data from IndexedDB
        if (!isOnline) {
          logger.fetch(`Offline mode: fetching topic ${topicId} from IndexedDB`);

          try {
            // Get topic from IndexedDB
            const topic = await db.topics.get(topicId);

            if (!topic || topic.isDeleted) {
              throw new Error(`Topic ${topicId} not found in offline storage`);
            }

            // Format the topic
            return formatTopic(topic as TopicData);
          } catch (offlineError) {
            logger.error(`Error fetching offline topic ${topicId}:`, offlineError);
            throw new Error('Failed to load topic from offline storage');
          }
        }

        // Online flow - fetch from server
        const { data, error } = await supabase
          .from('topics')
          .select('*')
          .eq('id', topicId)
          .single();

        if (error) throw error;

        // Store in IndexedDB for offline access
        try {
          await db.topics.put(data);
        } catch (storageError) {
          logger.error(`Error storing topic in IndexedDB:`, storageError);
          // Continue even if storage fails - this is non-critical
        }

        // Format and return the topic
        return formatTopic(data as TopicData);
      } catch (error) {
        const appError = handleError(error, {
          action: 'silent',
          context: { operation: 'fetchTopic', topicId, isOnline },
        });
        throw appError;
      }
    },
    enabled: !!topicId,
  });
}

/**
 * Hook for creating a new topic
 * Supports offline mode by storing in IndexedDB and queueing for sync
 */
export function useCreateTopic() {
  const queryClient = useQueryClient();
  // Get connection state without causing hook instance creation
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  return useMutation({
    // Optimistic update for topic creation
    onMutate: async (newTopic: CreateTopicInput) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: topicKeys.byProject(newTopic.projectId) });

      // Snapshot the previous value for rollback
      const previousTopics = queryClient.getQueryData<FormattedTopic[]>(
        topicKeys.byProject(newTopic.projectId)
      );

      // Create optimistic topic
      const topicId = crypto.randomUUID();
      const now = new Date().toISOString();
      const optimisticTopic: FormattedTopic = {
        id: topicId,
        projectId: newTopic.projectId,
        name: newTopic.name,
        description: newTopic.description,
        topicType: newTopic.topicType,
        lastUsedAt: now,
        createdAt: now,
        updatedAt: now,
        messages: [],
        metadata: {
          client_id: CLIENT_ID,
          created_at: now,
        },
      };

      // Optimistically update the topics list
      queryClient.setQueryData<FormattedTopic[]>(topicKeys.byProject(newTopic.projectId), old => {
        if (!old) return [optimisticTopic];
        return [optimisticTopic, ...old];
      });

      // Return context for rollback (including the optimistic topic for server sync)
      return { previousTopics, optimisticTopic };
    },

    mutationFn: async (newTopic: CreateTopicInput): Promise<FormattedTopic> => {
      logger.store('Adding new topic', {
        projectId: newTopic.projectId,
        name: newTopic.name,
        topicType: newTopic.topicType,
        is_online: isOnline,
      });

      const topicId = crypto.randomUUID();
      const now = new Date().toISOString();

      // Create topic data object
      const topicData: TopicData = {
        id: topicId,
        project_id: newTopic.projectId,
        name: newTopic.name,
        description: newTopic.description,
        topic_type: newTopic.topicType,
        last_used_at: now,
        created_at: now,
        updated_at: now,
        messages: [],
        metadata: {
          client_id: CLIENT_ID,
          created_at: now,
        },
      };

      // If offline, store locally and queue for sync
      if (!isOnline) {
        logger.store('Offline mode: Creating topic locally', { topicId });

        try {
          // Store in IndexedDB
          await db.topics.put(topicData);

          // Queue for sync when online
          await queueOfflineOperation('topics', 'create', topicId, topicData);

          // Return formatted topic
          return formatTopic(topicData);
        } catch (offlineError) {
          const error = handleError(offlineError, {
            action: 'toast',
            context: { operation: 'createTopic', mode: 'offline' },
          });
          throw error;
        }
      }

      // Online flow - create on server
      const { data, error } = await supabase.from('topics').insert([topicData]).select().single();

      if (error) throw error;

      // Store in IndexedDB for offline access
      try {
        await db.topics.put(data);
      } catch (storageError) {
        logger.error(`Error storing topic in IndexedDB:`, storageError);
        // Continue even if storage fails - this is non-critical
      }

      // Return formatted topic
      return formatTopic(data as TopicData);
    },

    // On error, rollback the optimistic update
    onError: (error, variables, context) => {
      // Restore the previous topics list
      if (context?.previousTopics) {
        queryClient.setQueryData(topicKeys.byProject(variables.projectId), context.previousTopics);
      }

      // Show user-friendly error message
      handleError(error, {
        action: 'toast',
        customMessage: 'Failed to create topic. Please try again.',
        context: { operation: 'createTopic_optimistic_rollback', projectId: variables.projectId },
      });
    },

    onSuccess: (newTopic, _variables, context) => {
      // Replace the optimistic topic with the real one from server
      queryClient.setQueryData<FormattedTopic[]>(topicKeys.byProject(newTopic.projectId), old => {
        if (!old) return [newTopic];

        // Find and replace the optimistic topic with the real one
        return old.map(topic => {
          // Match by the optimistic topic data from context
          if (
            context?.optimisticTopic &&
            topic.name === context.optimisticTopic.name &&
            topic.createdAt === context.optimisticTopic.createdAt
          ) {
            return newTopic;
          }
          return topic;
        });
      });

      // Add the new topic to the detail cache
      safeUpdateQueryData(queryClient, topicKeys.detail(newTopic.id), () => newTopic, {
        context: { operation: 'createTopic', topicId: newTopic.id },
      });
    },

    // Always invalidate after settle to ensure cache consistency
    onSettled: (data, error, variables) => {
      invalidateQuery(queryClient, topicKeys.byProject(variables.projectId), {
        refetchType: 'active',
        context: { operation: 'createTopic_settled', projectId: variables.projectId },
      });
    },
  });
}

/**
 * Hook for updating a topic
 * Supports offline mode by updating IndexedDB and queueing for sync
 *
 * @param {Object} mutation - The mutation parameters
 * @param {string} mutation.topicId - The ID of the topic to update (NOTE: parameter name is 'topicId', not 'id')
 * @param {UpdateTopicInput} mutation.updates - The updates to apply to the topic
 *
 * @example
 * ```typescript
 * const updateTopicMutation = useUpdateTopic();
 *
 * updateTopicMutation.mutate({
 *   topicId: 'topic-123',  // NOTE: Use 'topicId' not 'id'
 *   updates: {
 *     name: 'New Name',
 *     last_used_at: new Date().toISOString()
 *   }
 * });
 * ```
 */
export function useUpdateTopic() {
  const queryClient = useQueryClient();
  // Get connection state without causing hook instance creation
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  return useMutation({
    // Optimistic update for topic updates
    onMutate: async ({ topicId, updates }: { topicId: string; updates: UpdateTopicInput }) => {
      // Get the current topic to find its project ID
      const currentTopic = queryClient.getQueryData<FormattedTopic>(topicKeys.detail(topicId));

      if (!currentTopic) {
        logger.error('Cannot perform optimistic update: topic not found in cache', { topicId });
        return {};
      }

      const projectId = currentTopic.projectId;

      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: topicKeys.byProject(projectId) });
      await queryClient.cancelQueries({ queryKey: topicKeys.detail(topicId) });

      // Snapshot previous values for rollback
      const previousTopics = queryClient.getQueryData<FormattedTopic[]>(
        topicKeys.byProject(projectId)
      );
      const previousTopic = queryClient.getQueryData<FormattedTopic>(topicKeys.detail(topicId));

      // Apply optimistic updates to topic list
      if (previousTopics) {
        queryClient.setQueryData<FormattedTopic[]>(topicKeys.byProject(projectId), old => {
          if (!old) return old;

          return old.map(topic => {
            if (topic.id === topicId) {
              const updatedTopic = { ...topic };
              if (updates.name !== undefined) updatedTopic.name = updates.name;
              if (updates.description !== undefined) updatedTopic.description = updates.description;
              if (updates.topicType !== undefined) updatedTopic.topicType = updates.topicType;
              if (updates.messages !== undefined) updatedTopic.messages = updates.messages;
              updatedTopic.updatedAt = new Date().toISOString();
              if (updates.lastUsedAt) updatedTopic.lastUsedAt = updates.lastUsedAt;

              return updatedTopic;
            }
            return topic;
          });
        });
      }

      // Apply optimistic updates to individual topic cache
      if (previousTopic) {
        queryClient.setQueryData<FormattedTopic>(topicKeys.detail(topicId), old => {
          if (!old) return old;

          const updatedTopic = { ...old };
          if (updates.name !== undefined) updatedTopic.name = updates.name;
          if (updates.description !== undefined) updatedTopic.description = updates.description;
          if (updates.topicType !== undefined) updatedTopic.topicType = updates.topicType;
          if (updates.messages !== undefined) updatedTopic.messages = updates.messages;
          updatedTopic.updatedAt = new Date().toISOString();
          if (updates.lastUsedAt) updatedTopic.lastUsedAt = updates.lastUsedAt;

          return updatedTopic;
        });
      }

      // Return context for rollback
      return { previousTopics, previousTopic, projectId };
    },

    mutationFn: async ({ topicId, updates }: { topicId: string; updates: UpdateTopicInput }) => {
      if (!topicId) {
        throw new Error('Topic ID is required');
      }

      const now = new Date().toISOString();

      // Create a filtered updates object with only valid fields
      const filteredUpdates: Record<string, unknown> = {
        updated_at: now,
        last_used_at: updates.lastUsedAt || now,
      };

      if (updates.name) filteredUpdates.name = updates.name;
      if (updates.description) filteredUpdates.description = updates.description;
      if (updates.topicType) filteredUpdates.topic_type = updates.topicType;
      if (updates.messages) filteredUpdates.messages = updates.messages;

      // Add metadata
      filteredUpdates.metadata = {
        client_id: CLIENT_ID,
        updated_at: now,
      };

      // If offline, update locally and queue for sync
      if (!isOnline) {
        logger.store('Offline mode: Updating topic locally', { topicId });

        try {
          // Get current topic from IndexedDB
          const currentTopic = await db.topics.get(topicId);

          if (!currentTopic || currentTopic.isDeleted) {
            throw new Error(`Topic ${topicId} not found in offline storage`);
          }

          // Merge updates with current data
          const updatedTopic = {
            ...currentTopic,
            ...filteredUpdates,
          };

          // Update in IndexedDB
          await db.topics.put(updatedTopic);

          // Queue for sync when online
          await queueOfflineOperation('topics', 'update', topicId, updatedTopic);

          return { topicId, updates: filteredUpdates, timestamp: now };
        } catch (offlineError) {
          const error = handleError(offlineError, {
            action: 'toast',
            context: { operation: 'updateTopic', topicId, mode: 'offline' },
          });
          throw error;
        }
      }

      // Online flow - update on server
      const { data, error } = await supabase
        .from('topics')
        .update(filteredUpdates)
        .eq('id', topicId)
        .select()
        .single();

      if (error) throw error;

      // Update in IndexedDB for offline access
      try {
        // Get current data first
        const currentTopic = await db.topics.get(topicId);

        if (currentTopic) {
          await db.topics.put({
            ...currentTopic,
            ...data,
          });
        }
      } catch (storageError) {
        logger.error(`Error updating topic in IndexedDB:`, storageError);
        // Continue even if storage fails - this is non-critical
      }

      return { topicId, updates: filteredUpdates, timestamp: now, data };
    },
    onSuccess: result => {
      const { topicId, data } = result;

      // Get the topic data from the response or from the update payload
      const updatedTopicData = data as TopicData | undefined;

      // If we don't have the topic data from the server response, we still need the project ID
      // for cache updates, so get it from the queryClient cache
      let projectId: string | undefined;

      if (updatedTopicData) {
        projectId = updatedTopicData.project_id;
      } else {
        // Try to get project ID from the cache
        const cachedTopic = queryClient.getQueryData<FormattedTopic>(topicKeys.detail(topicId));
        if (cachedTopic) {
          projectId = cachedTopic.projectId;
        }
      }

      // Update topic detail cache
      const updatedTopic = safeUpdateQueryData<FormattedTopic>(
        queryClient,
        topicKeys.detail(topicId),
        currentTopic => {
          if (!currentTopic) return undefined;

          // If we have data from the server, use that for formatting
          if (updatedTopicData) {
            return formatTopic(updatedTopicData, currentTopic.isActive ? topicId : undefined);
          }

          // Otherwise use the updates
          return {
            ...currentTopic,
            ...(result.updates.name && { name: result.updates.name as string }),
            ...(result.updates.description && {
              description: result.updates.description as string,
            }),
            ...(result.updates.topicType && { topicType: result.updates.topicType }),
            ...(result.updates.messages && { messages: result.updates.messages }),
            updatedAt: result.timestamp,
            lastUsedAt: (result.updates.last_used_at as string) || result.timestamp,
          };
        },
        { context: { operation: 'updateTopic', topicId } }
      );

      // Update the topic in the project's topic list if we have the project ID
      if (projectId) {
        // Update topic in the project's topic list cache
        queryClient.setQueriesData(
          { queryKey: topicKeys.byProject(projectId) },
          (oldData: FormattedTopic[] | undefined) => {
            if (!oldData) return undefined;

            // Replace the topic in the list
            return oldData.map(topic => (topic.id === topicId ? updatedTopic || topic : topic));
          }
        );
      }

      // If we couldn't locate the project ID or the update wasn't successful,
      // we need to invalidate relevant queries to ensure data consistency
      if (!projectId || !updatedTopic) {
        logger.store('Could not update topic in cache, invalidating queries', {
          topicId,
          hasProjectId: !!projectId,
        });

        // Invalidate the topic detail query and all list queries for safety
        const queryKeysToInvalidate = new Map([
          [topicKeys.detail(topicId), { exact: true }],
          [topicKeys.lists(), { exact: false }],
        ]);

        batchInvalidateQueries(queryClient, queryKeysToInvalidate, {
          refetchType: 'active', // Only refetch if someone is watching
        });
      }
    },

    // On error, rollback the optimistic update
    onError: (error, variables, context) => {
      // Restore previous topic list
      if (context?.previousTopics && context?.projectId) {
        queryClient.setQueryData(topicKeys.byProject(context.projectId), context.previousTopics);
      }

      // Restore previous individual topic
      if (context?.previousTopic) {
        queryClient.setQueryData(topicKeys.detail(variables.topicId), context.previousTopic);
      }

      // Show user-friendly error message
      handleError(error, {
        action: 'toast',
        customMessage: 'Failed to update topic. Your changes have been reverted.',
        context: { operation: 'updateTopic_optimistic_rollback', topicId: variables.topicId },
      });
    },

    // Always invalidate after settle to ensure cache consistency
    onSettled: (_data, _error, variables, context) => {
      // Ensure topic cache is fresh regardless of outcome
      if (context?.projectId) {
        invalidateQuery(queryClient, topicKeys.byProject(context.projectId), {
          refetchType: 'active',
          context: { operation: 'updateTopic_settled', topicId: variables.topicId },
        });
      }

      invalidateQuery(queryClient, topicKeys.detail(variables.topicId), {
        refetchType: 'active',
        context: { operation: 'updateTopic_settled', topicId: variables.topicId },
      });
    },
  });
}

/**
 * Hook for appending a message to a topic
 * Mainly used for chat topics
 */
export function useAppendMessage() {
  const queryClient = useQueryClient();
  // Get connection state without causing hook instance creation
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();
  const updateTopic = useUpdateTopic();

  return useMutation({
    mutationFn: async ({ topicId, message }: { topicId: string; message: AppendMessageInput }) => {
      // Get the current topic first
      let currentTopic: FormattedTopic | undefined;

      try {
        const cachedTopic = queryClient.getQueryData<FormattedTopic>(topicKeys.detail(topicId));

        if (cachedTopic) {
          currentTopic = cachedTopic;
        } else {
          // If not in cache, try to get from DB
          if (!isOnline) {
            const offlineTopic = await db.topics.get(topicId);
            if (offlineTopic) {
              currentTopic = formatTopic(offlineTopic as TopicData);
            }
          } else {
            const { data, error } = await supabase
              .from('topics')
              .select('*')
              .eq('id', topicId)
              .single();

            if (!error && data) {
              currentTopic = formatTopic(data as TopicData);
            }
          }
        }

        if (!currentTopic) {
          throw new Error(`Topic ${topicId} not found`);
        }

        // Create the new message
        const newMessage = {
          id: crypto.randomUUID(),
          ...message,
        };

        // Append message using the update topic mutation
        return await updateTopic.mutateAsync({
          topicId,
          updates: {
            messages: [...(currentTopic.messages || []), newMessage],
            lastUsedAt: new Date().toISOString(),
          },
        });
      } catch (error) {
        const appError = handleError(error, {
          action: 'toast',
          context: { operation: 'appendMessage', topicId },
        });
        throw appError;
      }
    },
  });
}

/**
 * Hook for deleting a topic
 * Supports offline mode by marking as deleted in IndexedDB and queueing for sync
 */
export function useDeleteTopic() {
  const queryClient = useQueryClient();
  // Get connection state without causing hook instance creation
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  return useMutation({
    // Optimistic update for topic deletion
    onMutate: async (topicId: string) => {
      // Get the topic to find its project ID for cache updates
      const currentTopic = queryClient.getQueryData<FormattedTopic>(topicKeys.detail(topicId));

      if (!currentTopic) {
        logger.error('Cannot perform optimistic update: topic not found in cache', { topicId });
        return { topicId };
      }

      const projectId = currentTopic.projectId;

      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: topicKeys.byProject(projectId) });
      await queryClient.cancelQueries({ queryKey: topicKeys.detail(topicId) });

      // Snapshot previous values for rollback
      const previousTopics = queryClient.getQueryData<FormattedTopic[]>(
        topicKeys.byProject(projectId)
      );
      const previousTopic = queryClient.getQueryData<FormattedTopic>(topicKeys.detail(topicId));

      // Optimistically remove topic from topics list
      queryClient.setQueryData<FormattedTopic[]>(topicKeys.byProject(projectId), old => {
        if (!old) return old;
        return old.filter(topic => topic.id !== topicId);
      });

      // Remove topic from detail cache
      queryClient.removeQueries({
        queryKey: topicKeys.detail(topicId),
        exact: true,
      });

      // Return context for rollback
      return { previousTopics, previousTopic, projectId, topicId };
    },

    mutationFn: async (topicId: string) => {
      if (!topicId) {
        throw new Error('Topic ID is required');
      }

      // Get project ID before deletion (for cache updates)
      let projectId: string | undefined;

      // Look for topic in cache first
      const cachedTopic = queryClient.getQueryData<FormattedTopic>(topicKeys.detail(topicId));
      if (cachedTopic) {
        projectId = cachedTopic.projectId;
      }

      // If offline, mark as deleted locally and queue for sync
      if (!isOnline) {
        logger.store('Offline mode: Marking topic as deleted locally', { topicId });

        try {
          // Get current topic from IndexedDB if not already found
          if (!projectId) {
            const currentTopic = await db.topics.get(topicId);
            if (currentTopic) {
              projectId = currentTopic.project_id;
            }
          }

          // Mark as deleted in IndexedDB (we keep the record for sync)
          await db.topics.update(topicId, { isDeleted: true });

          // Queue for sync when online
          await queueOfflineOperation('topics', 'delete', topicId, { id: topicId });

          return { topicId, projectId };
        } catch (offlineError) {
          const error = handleError(offlineError, {
            action: 'toast',
            context: { operation: 'deleteTopic', topicId, mode: 'offline' },
          });
          throw error;
        }
      }

      // Online flow - delete on server
      // If we don't have projectId yet, fetch it
      if (!projectId) {
        try {
          const { data } = await supabase
            .from('topics')
            .select('project_id')
            .eq('id', topicId)
            .single();

          if (data) {
            projectId = data.project_id;
          }
        } catch (error) {
          // If we can't get the project ID, continue with deletion anyway
          logger.error('Error fetching project ID for topic deletion:', error);
        }
      }

      // Delete the topic
      const { error } = await supabase.from('topics').delete().eq('id', topicId);

      if (error) throw error;

      // Remove from IndexedDB
      try {
        await db.topics.delete(topicId);
      } catch (storageError) {
        logger.error(`Error deleting topic from IndexedDB:`, storageError);
        // Continue even if storage fails - this is non-critical
      }

      return { topicId, projectId };
    },

    // On error, rollback the optimistic update
    onError: (error, topicId, context) => {
      // Restore the previous topics list
      if (context?.previousTopics && context?.projectId) {
        queryClient.setQueryData(topicKeys.byProject(context.projectId), context.previousTopics);
      }

      // Restore the individual topic to detail cache
      if (context?.previousTopic) {
        queryClient.setQueryData(topicKeys.detail(topicId), context.previousTopic);
      }

      // Show user-friendly error message
      handleError(error, {
        action: 'toast',
        customMessage: 'Failed to delete topic. The topic has been restored.',
        context: { operation: 'deleteTopic_optimistic_rollback', topicId },
      });
    },
    onSuccess: result => {
      const { topicId, projectId } = result;

      // Remove topic from detail cache (already done optimistically, but ensure it's gone)
      queryClient.removeQueries({
        queryKey: topicKeys.detail(topicId),
        exact: true,
      });

      // If we have the project ID, ensure the topic is removed from project list
      if (projectId) {
        // Ensure the topic is removed from all query cache entries for this project
        queryClient.setQueriesData(
          { queryKey: topicKeys.byProject(projectId) },
          (oldData: FormattedTopic[] | undefined) => {
            if (!oldData) return oldData;
            return oldData.filter(topic => topic.id !== topicId);
          }
        );
      } else {
        // If we don't have the project ID, we need to update all topic lists
        // This is an exceptional case that should be rare, but we need to handle it
        logger.store('No project ID for deleted topic, invalidating all topic lists', { topicId });
        queryClient.invalidateQueries({
          queryKey: topicKeys.lists(),
          refetchType: 'active',
        });
      }
    },

    // Always invalidate after settle to ensure cache consistency
    onSettled: (data, error, topicId, context) => {
      // Ensure caches are consistent regardless of outcome
      if (context?.projectId) {
        invalidateQuery(queryClient, topicKeys.byProject(context.projectId), {
          refetchType: 'active',
          context: { operation: 'deleteTopic_settled', topicId },
        });
      }

      // Ensure topic detail cache is cleaned up
      queryClient.removeQueries({
        queryKey: topicKeys.detail(topicId),
        exact: true,
      });
    },
  });
}

/**
 * Hook for updating a topic's last used timestamp
 * Used when selecting a topic
 */
export function useUpdateLastUsedAt() {
  const updateTopic = useUpdateTopic();

  return useMutation({
    mutationFn: async (topicId: string) => {
      if (!topicId) {
        return null;
      }

      const now = new Date().toISOString();

      // Use the updateTopic mutation to update the timestamp
      return updateTopic.mutateAsync({
        topicId,
        updates: {
          lastUsedAt: now,
        },
      });
    },
  });
}
